/**
 * Pure logic for the /confirm-email-change link (SVC-1).
 *
 * The backend only accepts the token from the session that requested the
 * change (POST /api/user-profile/profile/email/confirm), and revokes every
 * session on success, so the user has to sign in again with the new address.
 */

export type ConfirmEmailChangeStatus =
  | 'waiting' // auth state still resolving
  | 'needs_login' // no session: sign in with the current email, then reopen the link
  | 'confirming'
  | 'success'
  | 'invalid' // missing/expired/foreign token
  | 'failed'; // transient error, retry possible

export function readTokenParam(raw: string | string[] | undefined): string {
  const value = Array.isArray(raw) ? raw[0] : raw;
  return (value || '').trim();
}

export function initialStatus(token: string, phase: string): ConfirmEmailChangeStatus {
  if (!token) return 'invalid';
  if (phase === 'loading') return 'waiting';
  if (phase === 'unauthenticated') return 'needs_login';
  return 'confirming';
}

export function statusForError(err: unknown): ConfirmEmailChangeStatus {
  const status = (err as { response?: { status?: number } } | null)?.response?.status;
  if (status === 401) return 'needs_login';
  if (status === 400 || status === 403) return 'invalid';
  return 'failed';
}

export async function runConfirmEmailChange(
  token: string,
  confirm: (token: string) => Promise<unknown>,
): Promise<ConfirmEmailChangeStatus> {
  try {
    await confirm(token);
    // Sessions are now revoked server-side; the success screen's button logs
    // out locally and routes to sign-in.
    return 'success';
  } catch (err) {
    return statusForError(err);
  }
}
